import { navigation } from '@good-season/platform';

import { Component, useState, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button.js';
import { SUPPORT_CODE_LEAD, diagnosticFor } from './diagnostic.js';
import { usePrimitiveStyles } from './styles.js';

/**
 * The last line between a crash and a white screen.
 *
 * What a person gets: what happened, what to try, the diagnostic code with a
 * button to copy it, and a way back. The default way back is a reload of the
 * same URL, which cannot help a crash that repeats on every render, so a host
 * with somewhere better to send the person passes `secondaryAction` (the web
 * app does at both of its boundaries: the scorebook goes back to the schedule,
 * the root goes to the start route). Without one, reload is all there is. What a person never gets: the exception
 * message, the stack, the component name, or a status code. The caught error
 * is held in state so a diagnostic code can be derived from it and is never
 * rendered.
 *
 * VISUAL TREATMENT IS PROVISIONAL. The handoff lists error states with
 * diagnostic codes as not yet designed. This is the structure and the copy the
 * voice rules dictate, set in the chassis type, and it is deliberately plain
 * so that it reads as unfinished rather than as a decision. A designer
 * replaces the treatment; the structure and the rules should survive.
 */

export interface ErrorSecondaryAction {
  readonly label: string;
  readonly onSelect: () => void;
}

export interface ErrorBoundaryProps {
  readonly children?: ReactNode | undefined;
  /**
   * Two uppercase letters naming the surface, used as the diagnostic prefix.
   * `SB` for the scorebook, matching the handoff's `SB-114` example.
   */
  readonly surface?: string | undefined;
  /** The way back. Defaults to reloading the screen. */
  readonly onReset?: (() => void) | undefined;
  readonly resetLabel?: string | undefined;
  /**
   * One extra way out, shown after the reset button. For a host that can send
   * the person somewhere else (a route, say) when the same screen keeps
   * crashing. The boundary does not clear its failure when this runs: the host
   * is expected to unmount or re-key the boundary by moving elsewhere.
   */
  readonly secondaryAction?: ErrorSecondaryAction | undefined;
  /** Report the failure somewhere. Never rendered. */
  readonly onError?: ((error: unknown, info: ErrorInfo) => void) | undefined;
  /**
   * `screen` when the fallback replaces a whole screen, so the page still has
   * its one `main` landmark and its one `h1`. The default sits inside a screen
   * that already has both, and adds neither.
   */
  readonly fills?: 'screen' | 'region' | undefined;
}

interface Caught {
  readonly value: unknown;
}

interface ErrorBoundaryState {
  /** Null while healthy. A wrapper, so that a thrown null still counts. */
  readonly caught: Caught | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { caught: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { caught: { value: error } };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly handleReset = (): void => {
    this.setState({ caught: null });
    const { onReset } = this.props;
    if (onReset !== undefined) {
      onReset();
      return;
    }
    // Through the adapter, not `window.location.reload()`. Under Capacitor
    // there is no URL bar and no reload button, and reloading a webview throws
    // away an event queue that has not been flushed. MOBILE_SUPPLEMENT.md 2.1
    // and ADR-0021.
    navigation.restart();
  };

  override render(): ReactNode {
    const { caught } = this.state;
    if (caught === null) return this.props.children;

    return (
      <ErrorFallback
        diagnostic={diagnosticFor(caught.value, this.props.surface)}
        resetLabel={this.props.resetLabel ?? 'Try again'}
        onReset={this.handleReset}
        secondaryAction={this.props.secondaryAction}
        fills={this.props.fills}
      />
    );
  }
}

export interface ErrorFallbackProps {
  readonly diagnostic: string;
  readonly resetLabel: string;
  readonly onReset: () => void;
  readonly secondaryAction?: ErrorSecondaryAction | undefined;
  readonly fills?: 'screen' | 'region' | undefined;
}

function clipboard(): { writeText(text: string): Promise<void> } | null {
  if (typeof navigator === 'undefined') return null;
  // Structural, because the clipboard is missing in plenty of real contexts
  // (an insecure origin, an old browser) that the DOM types insist it is not.
  const host: { clipboard?: { writeText(text: string): Promise<void> } } = navigator;
  return host.clipboard ?? null;
}

export function ErrorFallback({
  diagnostic,
  resetLabel,
  onReset,
  secondaryAction,
  fills = 'region',
}: ErrorFallbackProps): ReactNode {
  usePrimitiveStyles();
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    const target = clipboard();
    if (target === null) return;
    // On failure the code stays on screen and selectable, so nothing is lost.
    void target.writeText(diagnostic).then(
      () => {
        setCopied(true);
      },
      () => {
        setCopied(false);
      },
    );
  };

  const Title = fills === 'screen' ? 'h1' : 'h2';
  const fallback = (
    <section className="gs-ui gs-error" role="alert">
      <Title className="gs-error__title">This screen stopped</Title>
      <p className="gs-error__body">
        Anything already saved is safe. Try it again, and if it stops twice send this code along.
      </p>
      <hr className="gs-error__rule" />
      <p className="gs-diagnostic">
        <span>{SUPPORT_CODE_LEAD}</span> <span className="gs-diagnostic__code">{diagnostic}</span>
      </p>
      <div className="gs-error__actions">
        <Button onClick={onReset}>{resetLabel}</Button>
        {secondaryAction !== undefined && (
          <Button tone="ghost" onClick={secondaryAction.onSelect}>
            {secondaryAction.label}
          </Button>
        )}
        <Button tone="ghost" onClick={copy}>
          {copied ? 'Copied.' : 'Copy code'}
        </Button>
      </div>
    </section>
  );
  return fills === 'screen' ? <main>{fallback}</main> : fallback;
}
